import { z } from 'zod';

export type VideoMetadata = {
  duration: number;
  height: number;
  width: number;
};

const FALLBACK_WIDTH = 1280;
const FALLBACK_HEIGHT = 720;

const imgixVideoStreamSchema = z.object({
  codec_type: z.string(),
  duration: z.string().optional(),
  height: z.number().optional(),
  width: z.number().optional(),
});

const imgixVideoJsonSchema = z.object({
  streams: z.array(imgixVideoStreamSchema),
});

/**
 * Fetches video metadata (dimensions, duration) from an imgix video URL.
 * Uses the imgix `?fm=json` endpoint which returns a different schema for video files
 * (streams array with codec_type, width, height, duration).
 */
export const getVideoMetadata = async (url: string): Promise<VideoMetadata> => {
  try {
    const jsonUrl = new URL(url);
    jsonUrl.searchParams.set('fm', 'json');

    const response = await fetch(jsonUrl.toString());

    if (!response.ok) {
      throw new Error(`Imgix video metadata request failed: ${response.status}`);
    }

    const rawData: unknown = await response.json();
    const parseResult = imgixVideoJsonSchema.safeParse(rawData);

    if (!parseResult.success) {
      throw new Error(`Invalid imgix video response: ${parseResult.error.message}`);
    }

    const videoStream = parseResult.data.streams.find((s) => s.codec_type === 'video');

    if (!videoStream) {
      throw new Error('No video stream found in imgix response');
    }

    return {
      duration: videoStream.duration ? parseFloat(videoStream.duration) : 0,
      height: videoStream.height ?? FALLBACK_HEIGHT,
      width: videoStream.width ?? FALLBACK_WIDTH,
    };
  } catch (error) {
    console.warn('[getVideoMetadata] Failed to fetch video metadata, using fallback:', error);

    return {
      duration: 0,
      height: FALLBACK_HEIGHT,
      width: FALLBACK_WIDTH,
    };
  }
};
